import { difference } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { StressIncrementType } from '../../../../../Generated/Commodity-Management-Api';
import { MonthInteger } from '../../../../../Types/MonthDictionary';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import { StoneXButton, StoneXCheckbox, StoneXSelect, StoneXTextField } from '../../../../StoneXMui';
import { StoneXTransferList } from '../../../../StoneXMui/StoneXTransferList/StoneXTransferList';
import {
  ScenarioColumns,
  ScenarioReportColumn,
  ScenarioReportConfiguration,
  ScenarioReportSeries,
  ScenarioSeries,
  allColumns,
} from '../../Reports/Reports/Scenario/Types';
import { BaseReportSettingsPageProps } from './types';

const DEFAULT_SCENARIO_COLUMNS: ScenarioReportColumn[] = [...allColumns];
const DEFAULT_SCENARIO_SERIES = Object.keys(ScenarioSeries) as ScenarioReportSeries[];

function isScenarioColumn(column: string): column is ScenarioReportColumn {
  return column in ScenarioColumns;
}

function isScenarioSeries(series: string): series is ScenarioReportSeries {
  return series in ScenarioSeries;
}

function getUniqueKnownItems<T extends string>(items: string[], isKnownItem: (item: string) => item is T): T[] {
  const seen = new Set<T>();
  return items.reduce((result, item) => {
    if (!isKnownItem(item) || seen.has(item)) {
      return result;
    }

    seen.add(item);
    result.push(item);
    return result;
  }, [] as T[]);
}

function parseScenarioReportConfiguration(configurationJson: string): ScenarioReportConfiguration {
  return JSON.parse(configurationJson) as ScenarioReportConfiguration;
}

function getInitialAvailableColumns(configuration: ScenarioReportConfiguration): ScenarioReportColumn[] {
  if (!configuration.columns?.available?.length) {
    return DEFAULT_SCENARIO_COLUMNS;
  }

  const sanitized = getUniqueKnownItems(configuration.columns.available, isScenarioColumn);
  return sanitized.length ? sanitized : DEFAULT_SCENARIO_COLUMNS;
}

function getInitialActiveColumns(configuration: ScenarioReportConfiguration, availableColumns: ScenarioReportColumn[]): ScenarioReportColumn[] {
  const configuredOrDefault = configuration.columns?.active?.length
    ? getUniqueKnownItems(configuration.columns.active, isScenarioColumn)
    : availableColumns;

  return configuredOrDefault.filter((column) => availableColumns.includes(column));
}

function getInitialAvailableSeries(configuration: ScenarioReportConfiguration): ScenarioReportSeries[] {
  if (!configuration.chart?.series?.available?.length) {
    return DEFAULT_SCENARIO_SERIES;
  }

  const sanitized = getUniqueKnownItems(configuration.chart.series.available, isScenarioSeries);
  return sanitized.length ? sanitized : DEFAULT_SCENARIO_SERIES;
}

function getInitialActiveSeries(configuration: ScenarioReportConfiguration, availableSeries: ScenarioReportSeries[]): ScenarioReportSeries[] {
  const configuredOrDefault = configuration.chart?.series?.active?.length
    ? getUniqueKnownItems(configuration.chart.series.active, isScenarioSeries)
    : availableSeries;

  return configuredOrDefault.filter((series) => availableSeries.includes(series));
}

export default function ScenarioReportSettingsPage({ report, isSaving, onDeleteReport, onUpdateReport }: BaseReportSettingsPageProps) {
  const stressIncrementTypes = useMemo<StressIncrementType[]>(() => [StressIncrementType.Percent, StressIncrementType.Price], []);
  const monthOptions = useMemo<number[]>(() => Array.from({ length: 12 }, (_, idx) => idx + 1), []);

  const initialConfiguration = useMemo(() => parseScenarioReportConfiguration(report.configuration), [report.configuration]);
  const initialAvailableColumns = useMemo(() => getInitialAvailableColumns(initialConfiguration), [initialConfiguration]);
  const initialActiveColumns = useMemo(() => getInitialActiveColumns(initialConfiguration, initialAvailableColumns), [initialConfiguration, initialAvailableColumns]);
  const initialAvailableSeries = useMemo(() => getInitialAvailableSeries(initialConfiguration), [initialConfiguration]);
  const initialActiveSeries = useMemo(() => getInitialActiveSeries(initialConfiguration, initialAvailableSeries), [initialConfiguration, initialAvailableSeries]);

  const [reportName, setReportName] = useState<string>(report.reportName);
  const [configuration, setConfiguration] = useState<ScenarioReportConfiguration>(initialConfiguration);

  const [availableColumns, setAvailableColumns] = useState<ScenarioReportColumn[]>(initialAvailableColumns);
  const [unavailableColumns, setUnavailableColumns] = useState<ScenarioReportColumn[]>(difference(DEFAULT_SCENARIO_COLUMNS, initialAvailableColumns));
  const [activeColumns, setActiveColumns] = useState<ScenarioReportColumn[]>(initialActiveColumns);
  const [inactiveColumns, setInactiveColumns] = useState<ScenarioReportColumn[]>(difference(initialAvailableColumns, initialActiveColumns));

  const [availableSeries, setAvailableSeries] = useState<ScenarioReportSeries[]>(initialAvailableSeries);
  const [unavailableSeries, setUnavailableSeries] = useState<ScenarioReportSeries[]>(difference(DEFAULT_SCENARIO_SERIES, initialAvailableSeries));
  const [activeSeries, setActiveSeries] = useState<ScenarioReportSeries[]>(initialActiveSeries);
  const [inactiveSeries, setInactiveSeries] = useState<ScenarioReportSeries[]>(difference(initialAvailableSeries, initialActiveSeries));

  const [showBudgetStartDate, setShowBudgetStartDate] = useState<boolean>(!(initialConfiguration.inputs?.budgetStartDate?.hide ?? false));
  const [startDateStrategy, setStartDateStrategy] = useState<'fiscalYearStart' | 'currentMonth'>(initialConfiguration.inputs?.budgetStartDate?.strategy ?? 'fiscalYearStart');
  const [startDateOffset, setStartDateOffset] = useState<number>(initialConfiguration.inputs?.budgetStartDate?.offset ?? 0);
  const [rollOffMonth, setRollOffMonth] = useState<number | null>(initialConfiguration.inputs?.budgetStartDate?.rollOffInToNewYearMonth ?? null);

  const [showBudgetEndDate, setShowBudgetEndDate] = useState<boolean>(!(initialConfiguration.inputs?.budgetEndDate?.hide ?? false));
  const [endDateOffsetFromStart, setEndDateOffsetFromStart] = useState<number | undefined>(initialConfiguration.inputs?.budgetEndDate?.offsetFromStartMonth);

  const [stressIncrementType, setStressIncrementType] = useState<StressIncrementType>(initialConfiguration.inputs?.stressIncrementType ?? StressIncrementType.Percent);
  const [stressIncrementsText, setStressIncrementsText] = useState<string>((initialConfiguration.inputs?.stressIncrements ?? [5, 10, 15]).join(', '));
  const [allowMultipleCommodities, setAllowMultipleCommodities] = useState<boolean>(initialConfiguration.inputs?.commodities?.multiple ?? false);

  const columnOrderMap = useMemo<{ [key: string]: number }>(() => {
    const keys = Object.keys(ScenarioColumns);
    const map: { [key: string]: number } = {};
    keys.forEach((value: string, index) => (map[value] = index));
    return map;
  }, []);

  const seriesOrderMap = useMemo<{ [key: string]: number }>(() => {
    const keys = Object.keys(ScenarioSeries);
    const map: { [key: string]: number } = {};
    keys.forEach((value: string, index) => (map[value] = index));
    return map;
  }, []);

  useEffect(() => {
    const parsed = parseScenarioReportConfiguration(report.configuration);
    const currentAvailableColumns = getInitialAvailableColumns(parsed);
    const currentUnavailableColumns = difference(DEFAULT_SCENARIO_COLUMNS, currentAvailableColumns);
    const currentActiveColumns = getInitialActiveColumns(parsed, currentAvailableColumns);
    const currentInactiveColumns = difference(currentAvailableColumns, currentActiveColumns);

    const currentAvailableSeries = getInitialAvailableSeries(parsed);
    const currentUnavailableSeries = difference(DEFAULT_SCENARIO_SERIES, currentAvailableSeries);
    const currentActiveSeries = getInitialActiveSeries(parsed, currentAvailableSeries);
    const currentInactiveSeries = difference(currentAvailableSeries, currentActiveSeries);

    setReportName(report.reportName);
    setConfiguration(parsed);

    setAvailableColumns(currentAvailableColumns);
    setUnavailableColumns(currentUnavailableColumns);
    setActiveColumns(currentActiveColumns);
    setInactiveColumns(currentInactiveColumns);

    setAvailableSeries(currentAvailableSeries);
    setUnavailableSeries(currentUnavailableSeries);
    setActiveSeries(currentActiveSeries);
    setInactiveSeries(currentInactiveSeries);

    setShowBudgetStartDate(!(parsed.inputs?.budgetStartDate?.hide ?? false));
    setStartDateStrategy(parsed.inputs?.budgetStartDate?.strategy ?? 'fiscalYearStart');
    setStartDateOffset(parsed.inputs?.budgetStartDate?.offset ?? 0);
    setRollOffMonth(parsed.inputs?.budgetStartDate?.rollOffInToNewYearMonth ?? null);
    setShowBudgetEndDate(!(parsed.inputs?.budgetEndDate?.hide ?? false));
    setEndDateOffsetFromStart(parsed.inputs?.budgetEndDate?.offsetFromStartMonth);
    setStressIncrementType(parsed.inputs?.stressIncrementType ?? StressIncrementType.Percent);
    setStressIncrementsText((parsed.inputs?.stressIncrements ?? [5, 10, 15]).join(', '));
    setAllowMultipleCommodities(parsed.inputs?.commodities?.multiple ?? false);
  }, [report]);

  function onAvailableColumnsUpdate(updatedUnavailable: ScenarioReportColumn[], updatedAvailable: ScenarioReportColumn[]) {
    const filteredActiveColumns = activeColumns.filter((column) => updatedAvailable.includes(column));
    const updatedInactive = difference(updatedAvailable, filteredActiveColumns);

    setUnavailableColumns(updatedUnavailable);
    setAvailableColumns(updatedAvailable);
    setActiveColumns(filteredActiveColumns);
    setInactiveColumns(updatedInactive);
  }

  function onActiveColumnsUpdate(updatedInactive: ScenarioReportColumn[], updatedActive: ScenarioReportColumn[]) {
    setInactiveColumns(updatedInactive);
    setActiveColumns(updatedActive);
  }

  function onAvailableSeriesUpdate(updatedUnavailable: ScenarioReportSeries[], updatedAvailable: ScenarioReportSeries[]) {
    const filteredActiveSeries = activeSeries.filter((series) => updatedAvailable.includes(series));
    const updatedInactive = difference(updatedAvailable, filteredActiveSeries);

    setUnavailableSeries(updatedUnavailable);
    setAvailableSeries(updatedAvailable);
    setActiveSeries(filteredActiveSeries);
    setInactiveSeries(updatedInactive);
  }

  function onActiveSeriesUpdate(updatedInactive: ScenarioReportSeries[], updatedActive: ScenarioReportSeries[]) {
    setInactiveSeries(updatedInactive);
    setActiveSeries(updatedActive);
  }

  function resetConfiguration() {
    const parsed = parseScenarioReportConfiguration(report.configuration);
    const currentAvailableColumns = getInitialAvailableColumns(parsed);
    const currentUnavailableColumns = difference(DEFAULT_SCENARIO_COLUMNS, currentAvailableColumns);
    const currentActiveColumns = getInitialActiveColumns(parsed, currentAvailableColumns);
    const currentInactiveColumns = difference(currentAvailableColumns, currentActiveColumns);

    const currentAvailableSeries = getInitialAvailableSeries(parsed);
    const currentUnavailableSeries = difference(DEFAULT_SCENARIO_SERIES, currentAvailableSeries);
    const currentActiveSeries = getInitialActiveSeries(parsed, currentAvailableSeries);
    const currentInactiveSeries = difference(currentAvailableSeries, currentActiveSeries);

    setReportName(report.reportName);
    setConfiguration(parsed);

    setAvailableColumns(currentAvailableColumns);
    setUnavailableColumns(currentUnavailableColumns);
    setActiveColumns(currentActiveColumns);
    setInactiveColumns(currentInactiveColumns);

    setAvailableSeries(currentAvailableSeries);
    setUnavailableSeries(currentUnavailableSeries);
    setActiveSeries(currentActiveSeries);
    setInactiveSeries(currentInactiveSeries);

    setShowBudgetStartDate(!(parsed.inputs?.budgetStartDate?.hide ?? false));
    setStartDateStrategy(parsed.inputs?.budgetStartDate?.strategy ?? 'fiscalYearStart');
    setStartDateOffset(parsed.inputs?.budgetStartDate?.offset ?? 0);
    setRollOffMonth(parsed.inputs?.budgetStartDate?.rollOffInToNewYearMonth ?? null);
    setShowBudgetEndDate(!(parsed.inputs?.budgetEndDate?.hide ?? false));
    setEndDateOffsetFromStart(parsed.inputs?.budgetEndDate?.offsetFromStartMonth);
    setStressIncrementType(parsed.inputs?.stressIncrementType ?? StressIncrementType.Percent);
    setStressIncrementsText((parsed.inputs?.stressIncrements ?? [5, 10, 15]).join(', '));
    setAllowMultipleCommodities(parsed.inputs?.commodities?.multiple ?? false);
  }

  function saveConfiguration() {
    const stressIncrements = stressIncrementsText
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => !Number.isNaN(value));

    const nextConfiguration: ScenarioReportConfiguration = {
      ...configuration,
      inputs: {
        ...configuration.inputs,
        budgetStartDate: {
          hide: !showBudgetStartDate,
          strategy: startDateStrategy,
          offset: startDateOffset,
          rollOffInToNewYearMonth: (rollOffMonth as MonthInteger) ?? undefined,
        },
        budgetEndDate: {
          hide: !showBudgetEndDate,
          offsetFromStartMonth: endDateOffsetFromStart,
        },
        stressIncrementType,
        stressIncrements,
        commodities: {
          multiple: allowMultipleCommodities,
        },
      },
      columns: {
        available: availableColumns,
        active: activeColumns,
      },
      chart: {
        ...configuration.chart,
        series: {
          available: availableSeries,
          active: activeSeries,
        },
      },
    };

    onUpdateReport({
      ...report,
      reportName: reportName.trim(),
      configuration: JSON.stringify(nextConfiguration),
    });

    setConfiguration(nextConfiguration);
  }

  return (
    <div>
      <StoneXRow>
        <StoneXTextField value={reportName} label="Report Name" onUpdate={setReportName} />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <h3>Inputs</h3>
      </StoneXRow>

      <StoneXRow align='start'>
        <div style={{ flex: '1 1' }}>
          <StoneXRow>
            <StoneXCheckbox
              label="Show Budget Start Date"
              checked={showBudgetStartDate}
              onUpdate={setShowBudgetStartDate}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXSelect
              label="Start Strategy"
              helperText="The starting month for the report"
              options={['fiscalYearStart', 'currentMonth']}
              getId={(x) => x}
              getOptionLabel={(x) => (x === 'fiscalYearStart' ? 'Start of the year' : 'Current month')}
              value={startDateStrategy}
              onChange={(value) => setStartDateStrategy((value ?? 'fiscalYearStart') as 'fiscalYearStart' | 'currentMonth')}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXTextField
              label="Start Offset"
              helperText="The number of months from current month to start from"
              type="number"
              value={startDateOffset}
              onUpdate={(value) => setStartDateOffset(Number(value ?? 0))}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXSelect
              label="Roll-off Month"
              helperText="How many months before end of year to roll off to next year"
              options={monthOptions}
              getId={(x) => x}
              getOptionLabel={(x) => x}
              value={rollOffMonth}
              onChange={(value) => setRollOffMonth(value ?? null)}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Show Budget End Date"
              checked={showBudgetEndDate}
              onUpdate={setShowBudgetEndDate}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXTextField
              label="End Offset From Start"
              helperText="The number of months to run the report for"
              type="number"
              placeholder="12"
              value={endDateOffsetFromStart}
              onUpdate={(value) => setEndDateOffsetFromStart(Number(value))}
            />
          </StoneXRow>
        </div>

        <div style={{ flex: '1 1' }}>
          <StoneXRow>
            <StoneXSelect
              label="Stress Increment Type"
              options={stressIncrementTypes}
              getId={(x) => x}
              getOptionLabel={(x) => x}
              value={stressIncrementType}
              onChange={(value) => setStressIncrementType(value ?? StressIncrementType.Percent)}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXTextField
              label="Stress Increments"
              helperText="Comma-separated values, e.g. 5, 10, 15"
              value={stressIncrementsText}
              onUpdate={setStressIncrementsText}
            />
          </StoneXRow>
          <StoneXRow>
            <StoneXCheckbox
              label="Allow Multiple Commodities"
              checked={allowMultipleCommodities}
              onUpdate={setAllowMultipleCommodities}
            />
          </StoneXRow>
        </div>
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <h3>Chart Series</h3>
      </StoneXRow>
      <StoneXRow align='start'>
        <div style={{ flex: '1 1' }}>
          <p>Step 1: Choose which chart series are available.</p>
          <StoneXTransferList
            leftList={unavailableSeries}
            leftHeader="Unavailable Series"
            rightList={availableSeries}
            rightHeader="Available Series"
            getId={(x) => ScenarioSeries[x]?.seriesId ?? x}
            getGroup={(x) => ScenarioSeries[x]?.parentHeader}
            getSortingOrder={(x) => seriesOrderMap[x]}
            getOptionLabel={(x) => ScenarioSeries[x]?.columnName ?? ScenarioSeries[x]?.headerName ?? x}
            onUpdate={onAvailableSeriesUpdate}
          />
        </div>

        <div style={{ flex: '1 1' }}>
          <p>Step 2: From available series, choose which ones are active by default.</p>
          <StoneXTransferList
            leftList={inactiveSeries}
            leftHeader="Available but Inactive"
            rightList={activeSeries}
            rightHeader="Active Series"
            getId={(x) => ScenarioSeries[x]?.seriesId ?? x}
            getGroup={(x) => ScenarioSeries[x]?.parentHeader}
            getSortingOrder={(x) => seriesOrderMap[x]}
            getOptionLabel={(x) => ScenarioSeries[x]?.columnName ?? ScenarioSeries[x]?.headerName ?? x}
            onUpdate={onActiveSeriesUpdate}
          />
        </div>
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <h3>Columns</h3>
      </StoneXRow>
      <StoneXRow align='start'>
        <div style={{ flex: '1 1' }}>
          <p>Step 1: Choose which columns are available to the customer.</p>
          <StoneXTransferList
            leftList={unavailableColumns}
            leftHeader="Unavailable Columns"
            rightList={availableColumns}
            rightHeader="Available Columns"
            getId={(x) => ScenarioColumns[x]?.colId ?? x}
            getGroup={(x) => ScenarioColumns[x]?.parentHeader}
            getSortingOrder={(x) => columnOrderMap[x]}
            getOptionLabel={(x) => ScenarioColumns[x]?.columnName ?? ScenarioColumns[x]?.headerName ?? x}
            onUpdate={onAvailableColumnsUpdate}
          />
        </div>

        <div style={{ flex: '1 1' }}>
          <p>Step 2: From available columns, choose which ones are active by default.</p>
          <StoneXTransferList
            leftList={inactiveColumns}
            leftHeader="Available but Inactive"
            rightList={activeColumns}
            rightHeader="Active Columns"
            getId={(x) => ScenarioColumns[x]?.colId ?? x}
            getGroup={(x) => ScenarioColumns[x]?.parentHeader}
            getSortingOrder={(x) => columnOrderMap[x]}
            getOptionLabel={(x) => ScenarioColumns[x]?.columnName ?? ScenarioColumns[x]?.headerName ?? x}
            onUpdate={onActiveColumnsUpdate}
          />
        </div>
      </StoneXRow>

      <StoneXRow justify="end" marginDirection="top">
        <StoneXButton variant="outlined" onClick={() => onDeleteReport(report.customerReportId)}>
          Delete Report
        </StoneXButton>
        <StoneXButton variant="outlined" onClick={resetConfiguration}>Reset</StoneXButton>
        <StoneXButton isWorking={isSaving} onClick={saveConfiguration}>Save</StoneXButton>
      </StoneXRow>
    </div>
  );
}
